import { useQuery } from "@apollo/client";
import { CheckIcon, ClipboardIcon } from "@heroicons/react/24/outline";
import { useCallback, useState } from "react";

import { Table } from "../../../../../../components/Functional";
import { Button, Modal, getInput } from "../../../../../../components/ui";
import { UPDATE_VALUE as updateValue } from "../../../../../../graphql/mutation/Value";
import { FETCH_CURRENCIES } from "../../../../../../graphql/query/Currency";
import { FETCH_VALUES as fetchValues } from "../../../../../../graphql/query/Value";
import { FETCH_VALUE_TYPES } from "../../../../../../graphql/query/ValueType";
import { useEntities } from "../../../../../../hooks/useEntities";
import { formatRoundedAmount } from "../../../../../../util";
import SectionHeader from "../../components/SectionHeader";
import AddHistory from "./components/AddHistory";
import Header from "./components/Header";

// Value.typeId for "Sale Price" (mirrors the backend VALUE_TYPE_SALE_PRICE).
export const SALE_PRICE_TYPE_ID = 2;

// Id of the first Sale Price row in the (already date-desc sorted) list, so we
// highlight only the latest sale price. Returns null when there isn't one.
export const findFirstSalePriceId = (
  edges = [],
  salePriceTypeId = SALE_PRICE_TYPE_ID,
) => {
  const firstSalePrice = edges.find(
    edge => Number(edge?.typeId) === salePriceTypeId,
  );
  return firstSalePrice?.id ?? null;
};

// "USD 250,000" style text used by the copy-to-clipboard button.
export const buildValueCopyText = row => {
  const code = row?.currency?.code || "";
  const amount = formatRoundedAmount(row?.value);
  return [code, amount].filter(Boolean).join(" ");
};

const CopyValueButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable / blocked — leave the button state unchanged.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={!text}
      title={copied ? "Copied" : "Copy Price"}
      aria-label="Copy value"
      className="shrink-0 rounded border border-black bg-white p-1.5 text-black hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {copied ? (
        <CheckIcon className="h-4 w-4 text-green-600" />
      ) : (
        <ClipboardIcon className="h-4 w-4" />
      )}
    </button>
  );
};

const InstanceHistory = props => {
  const { instanceId, formik } = props || {};
  const { entities } = useEntities();
  const { data: { valueTypes = [] } = {} } = useQuery(FETCH_VALUE_TYPES);
  const { data: { currencies = [] } = {} } = useQuery(FETCH_CURRENCIES);
  const [topSalePriceId, setTopSalePriceId] = useState(null);

  const handleQueryStateChange = useCallback(({ edges }) => {
    setTopSalePriceId(findFirstSalePriceId(edges));
  }, []);

  const highlightTopSalePriceRow = row =>
    topSalePriceId != null && `${row?.id}` === `${topSalePriceId}`
      ? "bg-green-100"
      : "";

  const currencyOptions = currencies?.map(item => ({
    value: item?.id,
    label: item?.code,
  }));

  // Currency, value and a copy button rendered together as a single grouped
  // cell so they read as one "price" control.
  const renderValueGroup = cProps => {
    const { formik: rowFormik } = cProps || {};
    return (
      <div className="flex items-center gap-2">
        <div className="w-24 shrink-0">
          {getInput({
            formik: rowFormik,
            name: "currencyId",
            type: "multi-select",
            options: currencyOptions,
            label: null,
          })}
        </div>
        <div className="w-36 shrink-0">
          {getInput({
            formik: rowFormik,
            name: "value",
            type: "enhanced-currency",
            decimalPlaces: 0,
            label: null,
          })}
        </div>
        <CopyValueButton text={buildValueCopyText(rowFormik?.values)} />
      </div>
    );
  };

  const tableProps = {
    variant: "dynamicHeight",
    dataKey: "values",
    headers: [
      {
        inputType: "multi-select",
        label: "Type",
        name: "typeId",
        options: valueTypes?.map(item => ({
          value: item?.id,
          label: item?.description,
        })),
        type: "input",
      },
      {
        width: "300px",
        label: "Currency & Value",
        name: "value",
        type: "custom",
        component: renderValueGroup,
      },
      {
        width: "160px",
        inputType: "number-currency",
        label: "Max Discount",
        name: "maxDiscount",
        type: "input",
      },
      { inputType: "date", label: "Date", name: "date", type: "input" },
      {
        inputType: "multi-select",
        label: "Authorised",
        name: "authorisedId",
        type: "input",
        options: entities?.edges?.map(item => ({
          label: item.name,
          value: item.id,
        })),
      },
      { label: "Gross Profit", name: "grossProfit", type: "currency-rounded" },
      { label: "Notes", name: "notes", type: "label" },
      { label: "User", name: "user.name", type: "label" },
    ],
    UPDATE_MUTATION: updateValue(),
    // Re-run the list after an inline edit so server-side ordering (date DESC,
    // id DESC) is re-applied — e.g. changing a row's date moves it.
    REFETCH_QUERIES: ["FetchValues"],
    FETCH_QUERY: fetchValues({
      authorisedBy: true,
      currency: true,
      user: true,
    }),
    variables: {
      instanceId: +instanceId,
    },
    onQueryStateChange: handleQueryStateChange,
    rowClassName: highlightTopSalePriceRow,
  };

  const modalProps = {
    body: AddHistory,
    instanceId,
    hideCloseButton: true,
    scale: "md",
  };

  const historyActions = [
    {
      type: "black",
      label: "ADD RECORD",
      action: () => null,
      component: () => (
        <Modal {...modalProps}>
          <Button label="Add Valuation" />
        </Modal>
      ),
    },
  ];

  return (
    <div className="w-full">
      <SectionHeader title="History" actions={historyActions} />
      <Header formik={formik} />
      <div className="mb-6 w-full">
        <Table {...tableProps} />
      </div>
    </div>
  );
};

export default InstanceHistory;
